/**
 * Consent gate for every tracker on the marketing site.
 *
 * OneTrust exposes two independent signals: whether the visitor has answered
 * the banner and which cookie categories are active
 * (`window.OnetrustActiveGroups`). In opt-out and notice-only regions every
 * category is active before the visitor touches the banner, so a tracker may
 * run only when both signals hold: the banner was answered and the tracker's
 * category is active. Missing OneTrust means no consent.
 *
 * The one exception is our own first-party analytics: outside the opt-in model,
 * Statsig events (AbTestingProvider), the GA4 tag (ga4Tag) and GA4 Measurement
 * Protocol beacons (trackInteractionCore) run before the answer while
 * Performance is active and the browser sends no Global Privacy Control. That
 * rule is `firstPartyAnalyticsAllowed`; the Statsig provider composes it from
 * `consentModel()` and `categoryActive()` because it also creates a holding
 * client under Global Privacy Control.
 *
 * An answer is known three ways: a `consent.onetrust` event that follows a
 * click inside OneTrust's UI on this page, the SDK's own closed state
 * (`IsAlertBoxClosedAndValid`), and the `OptanonAlertBoxClosed` cookie before
 * the SDK loads. The event matters because the SDK writes its cookies for
 * mightynetworks.com only, so on a deploy preview or localhost the closed state
 * never flips and the event is the only record of the click.
 *
 * The event alone is not an answer. OneTrust also dispatches it on page load
 * whenever the `OptanonConsent` cookie already holds active groups, and in an
 * opt-out or notice-only region that cookie is written on the very first visit,
 * before the visitor touches the banner. Only an event preceded by a click
 * inside `#onetrust-consent-sdk` (the banner and the preference center) counts.
 */

import { globalPrivacyControl } from './globalPrivacyControl';

export const CONSENT_CATEGORIES = {
  performance: 'C0002',
  functional: 'C0003',
  targeting: 'C0004',
} as const;

export type ConsentCategory = (typeof CONSENT_CATEGORIES)[keyof typeof CONSENT_CATEGORIES];

type ActiveGroups = string | string[];

/**
 * OneTrust's consent model for the visitor's region, set per geolocation rule
 * in the OneTrust console. `opt-in` is the one model the code treats
 * differently: nothing but Strictly Necessary is active until the visitor
 * accepts, and no tracker touches the device before then. Under the others
 * (`opt-out`, `notice only`) every category is active from the first page
 * load, and the Statsig client starts before the answer and sends its events
 * unless Global Privacy Control is on; they are not keys here because no code
 * tells them apart.
 */
export const CONSENT_MODELS = {
  optIn: 'opt-in',
} as const;

type OneTrustWindow = Window & {
  OneTrust?: {
    IsAlertBoxClosed?: () => boolean;
    IsAlertBoxClosedAndValid?: () => boolean;
    GetDomainData?: () => { ConsentModel?: { Name?: string } } | undefined;
  };
  OnetrustActiveGroups?: string;
  OptanonWrapper?: () => void;
};

type ConsentListener = (activeGroups?: ActiveGroups) => void;

const listeners = new Set<ConsentListener>();
let unwire: (() => void) | null = null;
let installedWrapper: (() => void) | null = null;
let clickedOneTrustUi = false;
let answeredOnThisPage = false;

// Every OneTrust control the visitor can act on (banner buttons, the close
// icon, preference center toggles and save) renders inside this element.
const ONETRUST_UI_SELECTOR = '#onetrust-consent-sdk';

const getWindow = (): OneTrustWindow | null => (typeof window === 'undefined' ? null : (window as OneTrustWindow));

// The click grants nothing by itself. It only tells a `consent.onetrust` the
// visitor caused apart from the one OneTrust fires on page load: the answer
// is the event that follows the click, and whether a category is granted is
// still decided by the active groups, so Reject All is an answer that grants
// nothing. Both listeners are installed at module load, before OneTrust is
// injected, so no subscriber has to exist for the click to count. The click is
// observed in the capture phase, so a control that stops propagation still
// counts.
getWindow()?.document.addEventListener(
  'click',
  (event) => {
    const target = event.target as Element | null;
    if (target && typeof target.closest === 'function' && target.closest(ONETRUST_UI_SELECTOR)) {
      clickedOneTrustUi = true;
    }
  },
  true
);

getWindow()?.addEventListener('consent.onetrust', () => {
  if (clickedOneTrustUi) {
    answeredOnThisPage = true;
  }
});

const answeredCookiePresent = (): boolean =>
  typeof document !== 'undefined' && /(^|;\s*)OptanonAlertBoxClosed=/.test(document.cookie);

// An answer given on this page counts whatever the SDK persisted. Otherwise,
// once the SDK is loaded it is the authority: `IsAlertBoxClosedAndValid` is
// false while a re-consent prompt is pending, even though the visitor's earlier
// answer is still in the cookie. The cookie decides only before the SDK loads.
const bannerAnswered = (w: OneTrustWindow): boolean => {
  if (answeredOnThisPage) {
    return true;
  }
  const oneTrust = w.OneTrust;
  if (!oneTrust) {
    return answeredCookiePresent();
  }
  if (typeof oneTrust.IsAlertBoxClosedAndValid === 'function') {
    return oneTrust.IsAlertBoxClosedAndValid();
  }
  if (typeof oneTrust.IsAlertBoxClosed === 'function') {
    return oneTrust.IsAlertBoxClosed();
  }
  return answeredCookiePresent();
};

/**
 * True when the visitor has answered the banner on this or an earlier visit,
 * whether or not OneTrust has reported their groups yet.
 */
export const bannerAnsweredBefore = (): boolean => {
  const w = getWindow();
  return !!w && (answeredCookiePresent() || bannerAnswered(w));
};

/**
 * The consent model OneTrust applies to this visitor, lowercased
 * (`CONSENT_MODELS.optIn` or another OneTrust model name), or `null` until the
 * full SDK has loaded. The stub that runs before the SDK exposes no domain data.
 */
export const consentModel = (): string | null => {
  const w = getWindow();
  const getDomainData = w?.OneTrust?.GetDomainData;
  if (typeof getDomainData !== 'function') {
    return null;
  }
  const name = getDomainData()?.ConsentModel?.Name;
  return typeof name === 'string' && name ? name.toLowerCase() : null;
};

/**
 * True when `category` is among the groups OneTrust reports as active, whether
 * or not the visitor has answered the banner. In an opt-out or notice-only
 * region this is the state before any click; once the visitor has answered,
 * `hasConsent` is the authority.
 */
export const categoryActive = (category: ConsentCategory): boolean => {
  const groups = getWindow()?.OnetrustActiveGroups ?? '';
  return groups.indexOf(category) !== -1;
};

/**
 * True once the visitor has answered the banner and `category` is active.
 * `activeGroups` overrides `window.OnetrustActiveGroups`; `indexOf` works on
 * both the string OneTrust stores there and the array it sends with
 * `OneTrustGroupsUpdated`.
 */
export const hasConsent = (category: ConsentCategory, activeGroups?: ActiveGroups): boolean => {
  const w = getWindow();
  if (!w || !bannerAnswered(w)) {
    return false;
  }
  const groups = activeGroups ?? w.OnetrustActiveGroups ?? '';
  return groups.indexOf(category) !== -1;
};

/**
 * True when our own first-party analytics (the GA4 tag, GA4 Measurement
 * Protocol beacons, Statsig events) may run right now: Performance granted,
 * or, outside the opt-in model, the banner not answered yet, Performance
 * active and no Global Privacy Control. False until OneTrust's SDK has loaded,
 * since the model is unknown before then. `static/scripts/google-consent-mode.init.js`
 * applies the same rule to `analytics_storage`, and the early-start condition in
 * AbTestingProvider repeats it, so both must change with this.
 */
export const firstPartyAnalyticsAllowed = (): boolean => {
  if (hasConsent(CONSENT_CATEGORIES.performance)) {
    return true;
  }
  const model = consentModel();
  return (
    !!model &&
    model !== CONSENT_MODELS.optIn &&
    !bannerAnsweredBefore() &&
    categoryActive(CONSENT_CATEGORIES.performance) &&
    !globalPrivacyControl()
  );
};

const notify = (activeGroups?: ActiveGroups): void => {
  Array.from(listeners).forEach((listener) => listener(activeGroups));
};

const wire = (w: OneTrustWindow): void => {
  // The Head script can reassign window.OptanonWrapper (Gatsby re-runs it on
  // client-side navigation). Re-wrap whatever is there now so the chain holds.
  if (unwire && w.OptanonWrapper !== installedWrapper) {
    unwire();
  }
  if (unwire) {
    return;
  }

  const onGroupsUpdated = (event: Event): void => notify((event as CustomEvent<string[]>).detail);
  const onConsentChanged = (): void => notify();
  w.addEventListener('OneTrustGroupsUpdated', onGroupsUpdated);
  w.addEventListener('consent.onetrust', onConsentChanged);

  // OneTrust also calls OptanonWrapper on load and after every banner answer,
  // which covers SDK versions that dispatch neither event.
  const previousWrapper = w.OptanonWrapper;
  const wrapper = (): void => {
    if (typeof previousWrapper === 'function') {
      previousWrapper();
    }
    notify();
  };
  w.OptanonWrapper = wrapper;
  installedWrapper = wrapper;

  unwire = () => {
    w.removeEventListener('OneTrustGroupsUpdated', onGroupsUpdated);
    w.removeEventListener('consent.onetrust', onConsentChanged);
    if (w.OptanonWrapper === wrapper) {
      w.OptanonWrapper = previousWrapper;
    }
    installedWrapper = null;
    unwire = null;
  };
};

/**
 * Runs `callback` once the visitor has consented to `category`: immediately
 * when consent is already in place, otherwise on the first OneTrust update
 * that grants it. Never runs when OneTrust never loads or the visitor declines.
 * Returns an unsubscribe function.
 */
export const onConsentGranted = (category: ConsentCategory, callback: () => void): (() => void) => {
  const w = getWindow();
  if (!w) {
    return () => undefined;
  }

  if (hasConsent(category)) {
    callback();
    return () => undefined;
  }

  const listener: ConsentListener = (activeGroups) => {
    if (!hasConsent(category, activeGroups)) {
      return;
    }
    listeners.delete(listener);
    callback();
  };

  listeners.add(listener);
  wire(w);

  return () => {
    listeners.delete(listener);
  };
};

/**
 * Runs `callback` with whether the visitor currently consents to `category`:
 * once at subscription, then on every OneTrust signal whose answer differs
 * from the last one delivered. Covers consent withdrawn after it was granted,
 * which `onConsentGranted` does not report. Returns an unsubscribe function.
 */
export const onConsentChanged = (category: ConsentCategory, callback: (granted: boolean) => void): (() => void) => {
  const w = getWindow();
  if (!w) {
    return () => undefined;
  }

  let granted = hasConsent(category);
  callback(granted);

  const listener: ConsentListener = (activeGroups) => {
    const next = hasConsent(category, activeGroups);
    if (next === granted) {
      return;
    }
    granted = next;
    callback(next);
  };

  listeners.add(listener);
  wire(w);

  return () => {
    listeners.delete(listener);
  };
};

/**
 * Runs `callback` once with the consent model OneTrust applies to this
 * visitor: immediately when the SDK has loaded, otherwise on the first OneTrust
 * signal after it has. Never runs when OneTrust never loads. Returns an
 * unsubscribe function.
 */
export const onConsentModelKnown = (callback: (model: string) => void): (() => void) => {
  const w = getWindow();
  if (!w) {
    return () => undefined;
  }

  const known = consentModel();
  if (known) {
    callback(known);
    return () => undefined;
  }

  const listener: ConsentListener = () => {
    const model = consentModel();
    if (!model) {
      return;
    }
    listeners.delete(listener);
    callback(model);
  };

  listeners.add(listener);
  wire(w);

  return () => {
    listeners.delete(listener);
  };
};

/**
 * Runs `callback` once, on the first signal where the visitor has answered the
 * banner and OneTrust has reported its active groups: immediately when both
 * already hold. `granted` says whether `category` is active. Returns an
 * unsubscribe function.
 */
export const onConsentDecided = (category: ConsentCategory, callback: (granted: boolean) => void): (() => void) => {
  const w = getWindow();
  if (!w) {
    return () => undefined;
  }

  const decided = (activeGroups?: ActiveGroups): boolean =>
    bannerAnswered(w) && (activeGroups ?? w.OnetrustActiveGroups) !== undefined;

  if (decided()) {
    callback(hasConsent(category));
    return () => undefined;
  }

  const listener: ConsentListener = (activeGroups) => {
    if (!decided(activeGroups)) {
      return;
    }
    listeners.delete(listener);
    callback(hasConsent(category, activeGroups));
  };

  listeners.add(listener);
  wire(w);

  return () => {
    listeners.delete(listener);
  };
};

// Test helper - drops pending listeners and restores the window hooks
export const __resetConsentForTesting__ = (): void => {
  listeners.clear();
  clickedOneTrustUi = false;
  answeredOnThisPage = false;
  if (unwire) {
    unwire();
  }
};
